import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck, MessageCircle, ShieldCheck, Lock, Clock, ClipboardCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { EnquiryForm } from "./EnquiryForm";
import { ServiceIcon } from "./ServiceIcon";
import { Reveal } from "./Reveal";
import { bookingUrl, whatsappUrl } from "@/data/site";

export const Eyebrow = ({ children, light }) => <p className={`eyebrow ${light ? "is-light" : ""}`}>{children}</p>;

export const PageHero = ({ eyebrow, title, children, pills, testid }) => (
  <section className="relative overflow-hidden bg-cream py-20 md:py-24" data-testid={testid}>
    <div className="pointer-events-none absolute -left-28 -top-24 h-96 w-96 rounded-full bg-brand-blue/10 blur-3xl" aria-hidden="true" />
    <div className="pointer-events-none absolute right-[-8%] bottom-[-20%] h-[28rem] w-[28rem] rounded-full bg-brand-red/5 blur-3xl" aria-hidden="true" />
    <div className="container-x relative max-w-3xl">
      <Reveal>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="mt-5 font-display text-4xl leading-[1.05] text-navy sm:text-5xl lg:text-6xl">{title}</h1>
        {children && <p className="mt-5 max-w-2xl text-slate-600 md:text-lg">{children}</p>}
        {pills && <div className="mt-8 flex flex-wrap gap-2.5">{pills.map(([Icon, t]) => <span key={t} className="trust-pill"><Icon className="h-4 w-4 text-brand-red" /> {t}</span>)}</div>}
      </Reveal>
    </div>
  </section>
);

export const FaqList = ({ faqs, id = "faq" }) => (
  <Accordion type="single" collapsible className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-white shadow-premium" data-testid={`${id}-accordion`}>
    {faqs.map((f, i) => (
      <AccordionItem key={f.q} value={`q${i}`} className="border-0 px-5">
        <AccordionTrigger className="py-5 text-left text-base font-semibold text-navy hover:no-underline hover:text-brand-red" data-testid={`${id}-item-${i}`}>{f.q}</AccordionTrigger>
        <AccordionContent className="pb-5 text-[15px] leading-relaxed text-slate-600">{f.a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export const EnquirySection = ({ defaultService, title = "Not sure which path is right for you?", id = "enquiry" }) => (
  <section id={id} className="navy-depth relative overflow-hidden py-20 md:py-28 scroll-mt-24" data-testid="enquiry-section">
    <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-brand-blue/20 blur-3xl" aria-hidden="true" />
    <div className="pointer-events-none absolute -right-20 bottom-0 h-80 w-80 rounded-full bg-brand-red/15 blur-3xl" aria-hidden="true" />
    <div className="container-x relative grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
      <div className="text-white">
        <Eyebrow light>Start with your situation</Eyebrow>
        <h2 className="mt-4 font-display text-3xl leading-tight sm:text-4xl lg:text-[2.75rem]">{title}</h2>
        <p className="mt-5 max-w-md text-white/70">Tell us about your goal and current status. Our team will review your enquiry and contact you regarding the next steps.</p>
        <ul className="mt-8 space-y-4 text-sm">
          {[[ShieldCheck, "Reviewed by our team, not an automated system"], [Lock, "Your information is kept confidential"], [Clock, "No obligation. Required fields are marked *"]].map(([I, t]) => (
            <li key={t} className="flex items-center gap-3 text-white/85"><span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/10 text-brand-blue ring-1 ring-white/15"><I className="h-4 w-4" /></span>{t}</li>
          ))}
        </ul>
      </div>
      <div className="rounded-2xl bg-white p-6 shadow-premium-lg ring-1 ring-black/5 sm:p-8"><EnquiryForm defaultService={defaultService} /></div>
    </div>
  </section>
);

export const CtaBanner = ({ service }) => (
  <section className="bg-cream py-20" data-testid="cta-banner">
    <div className="container-x">
      <Reveal className="relative overflow-hidden rounded-3xl bg-navy px-8 py-14 shadow-premium-lg md:px-14">
        <div className="navy-depth pointer-events-none absolute inset-0 opacity-70" aria-hidden="true" />
        <div className="pointer-events-none absolute -right-10 -top-10 h-56 w-56 rounded-full border-[28px] border-white/5" aria-hidden="true" />
        <div className="relative flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl text-white">
            <Eyebrow light>Ready to start?</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl">Let's discuss your immigration goals.</h2>
            <p className="mt-4 text-white/75">Every situation is different. Book a paid 15-minute consultation, online or in person, for a professional assessment of your next steps.</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <a href={bookingUrl()} target="_blank" rel="noopener noreferrer" className="btn-primary justify-center" data-testid="cta-book-btn"><CalendarCheck className="h-4 w-4" /> Book a Consultation</a>
            <a href={whatsappUrl(service)} target="_blank" rel="noopener noreferrer" className="btn-white justify-center" data-testid="cta-whatsapp-btn"><MessageCircle className="h-4 w-4" /> Chat on WhatsApp</a>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export const ServiceCard = ({ s, i = 0 }) => (
  <Link to={`/services/${s.slug}`} className="group relative flex flex-col overflow-hidden rounded-2xl border border-line bg-white p-6 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-navy/15 hover:shadow-premium" data-testid={`service-card-${s.slug}`}>
    <span className="pointer-events-none absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-brand-red transition-transform duration-300 group-hover:scale-x-100" aria-hidden="true" />
    <span className="grid h-12 w-12 place-items-center rounded-xl bg-cream text-brand-red ring-1 ring-brand-red/10 transition-colors duration-300 group-hover:bg-brand-red group-hover:text-white"><ServiceIcon name={s.icon} /></span>
    <h3 className="mt-5 text-lg font-bold text-navy">{s.name}</h3>
    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{s.short}</p>
    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-red">Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
  </Link>
);

const TeaserCard = ({ to, icon: I, eyebrow, title, text, cta, testid, dark }) => (
  <Link to={to} data-testid={testid} className={`group relative flex flex-col justify-between overflow-hidden rounded-3xl p-8 shadow-premium transition-transform duration-300 hover:-translate-y-1 md:p-10 ${dark ? "navy-depth text-white" : "border border-line bg-white text-navy"}`}>
    <span className={`grid h-12 w-12 place-items-center rounded-2xl ${dark ? "bg-white/10" : "bg-cream text-brand-red"}`}><I className="h-6 w-6" /></span>
    <div className="mt-8"><Eyebrow light={dark}>{eyebrow}</Eyebrow><h3 className="mt-3 font-display text-2xl sm:text-3xl">{title}</h3><p className={`mt-3 ${dark ? "text-white/75" : "text-slate-600"}`}>{text}</p></div>
    <span className="mt-8 inline-flex items-center gap-2 font-semibold text-brand-red">{cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
  </Link>
);

export const LeadTeaser = () => (
  <section className="bg-[#faf8f5] py-20 md:py-24" data-testid="lead-teaser">
    <div className="container-x grid gap-6 md:grid-cols-2">
      <Reveal><TeaserCard to="/eligibility-check" icon={ClipboardCheck} eyebrow="1-minute quiz" title="Check your eligibility for free." text="Answer 8 quick questions and see which Canadian pathways may fit your profile." cta="Start the check" testid="teaser-eligibility-link" /></Reveal>
      <Reveal delay={100}><TeaserCard dark to="/free-consultation" icon={CalendarCheck} eyebrow="No obligation" title="Request a free consultation." text="Pick a date and time that suits you: phone, WhatsApp, video or in person." cta="Request a time" testid="teaser-consultation-link" /></Reveal>
    </div>
  </section>
);
